import { useEffect, useState } from 'react';
import { Menu } from 'lucide-react';
import { useLocation, useMatches } from 'react-router';
import { UserButton } from '@clerk/clerk-react';
import { Button, cn } from '@bill-alps/ui';
import { useTranslation } from '../../i18n/useTranslation';
import { resolveMobileTitle } from '@/lib/routing/mobile-route-title';
import { LanguageSwitcher } from '@/components/language-switcher';

interface MobileHeaderProps {
  isSupport: boolean;
  onOpenMenu: () => void;
}

export function MobileHeader({ isSupport, onOpenMenu }: MobileHeaderProps) {
  const { t } = useTranslation();
  const matches = useMatches();
  const location = useLocation();
  const [isCompact, setIsCompact] = useState(() =>
    typeof window === 'undefined' ? false : window.scrollY > 8,
  );

  useEffect(() => {
    const updateCompactState = () => setIsCompact(window.scrollY > 8);

    updateCompactState();
    window.addEventListener('scroll', updateCompactState, { passive: true });
    return () => window.removeEventListener('scroll', updateCompactState);
  }, [location.key]);

  const titleKey = resolveMobileTitle(matches, { isSupport });

  return (
    <header
      data-mobile-header
      data-compact={isCompact ? 'true' : 'false'}
      className={cn(
        'sticky top-[var(--global-banners-height)] z-40 grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center border-b-2 border-border bg-background transition-[height,padding,gap] duration-200 motion-reduce:transition-none md:hidden',
        isCompact ? 'h-11 gap-2 px-3' : 'h-14 gap-3 px-4',
      )}
    >
      <Button
        variant="link"
        size="icon"
        hoverEffect="none"
        aria-label="Menu"
        className="shrink-0"
        onClick={onOpenMenu}
      >
        <Menu className="size-5" aria-hidden="true" />
      </Button>
      <h1
        data-mobile-header-title
        className={cn(
          'min-w-0 truncate text-center font-bold leading-tight transition-[font-size] duration-200 motion-reduce:transition-none',
          isCompact ? 'text-base' : 'text-lg',
        )}
      >
        {t(titleKey)}
      </h1>
      <div className="flex shrink-0 items-center gap-3">
        <LanguageSwitcher />
        <UserButton />
      </div>
    </header>
  );
}
